import React, { PureComponent } from 'react'

import { DEFAULT_SITE_REF_ID } from '../../../constants';
import { createFormBag, FormBag, FormData, FormErrors, FormUpdateEvent, validateFormBag } from 'react-formage'
import { Button, ButtonGroup } from 'components/ui/button'
import { FieldSection } from 'components/ui/fieldset'
import { Input } from 'components/ui/input'
import { SelectRef } from 'components/ui/select'
import { Modal } from 'components/ui/modal'
import { StatusGuard } from 'components/ui/status-guard'

import { sharedActionCreators } from 'containers/shared'
import { connect } from 'containers/store'

import { Site } from 'generated/mos/structure'
import { Tour } from 'generated/mos/org/getty/audio'
import { CreateTourRequest } from 'generated/mos/org/getty/audiomanagement'

import { pick } from 'helpers/core'
import { Status } from 'helpers/status'

import { actionCreators } from '../../../actions'
import { GettyStore } from '../../../store'
import { tourSchema } from '../../../schemas/tour'

type ConnectedProps = {
  tourCreate: GettyStore['getty']['tourCreate'];
  tourUpdate: GettyStore['getty']['tourUpdate'];
  sites: GettyStore['getty']['sites'];
};

type DirectProps = {
  readonly historyPush: (id: string) => void;
  readonly onClose: () => void;
  readonly onSave: () => void;
  readonly projectId: string;
}

type ActionProps =
  Pick<typeof actionCreators, 'gettyCreateTourRequest'> &
  Pick<typeof actionCreators, 'gettyListSitesRequest'> &
  Pick<typeof sharedActionCreators, 'toastNotification'>;

type Props = ConnectedProps & DirectProps & ActionProps;

type FormValues = {
  readonly tourName: string;
  readonly siteRef: Site.Ref | undefined;
  readonly description: string;
};

type State  = {
  readonly bag: FormBag<FormValues>;
}

class TourCreateView extends PureComponent<Props, State> {
  public constructor(props: Props) {
    super(props)
    this.state = {
      bag: createFormBag({
        tourName: '',
        siteRef: undefined,
        description: '',
      }),
    }
  }

  public componentDidMount() {
    this.props.gettyListSitesRequest()
  }

  public componentDidUpdate() {
    const {
      historyPush,
      onClose,
      projectId,
      toastNotification,
      tourCreate,
      tourUpdate,
      sites,
    } = this.props

    if (tourCreate.status === Status.Failed) {
      toastNotification({ type: 'error', text: 'There was a problem creating the tour.' })
    }

    if (
      tourCreate.status === Status.Complete &&
      tourUpdate.status === Status.Stale &&
      tourUpdate.data.tour
    ) {
      toastNotification({ type: 'success', text: 'Your tour has been created.' })
      onClose()
      const tourId = Tour.mustRef(tourUpdate.data.tour.ref).id
      historyPush(`/project/${projectId}/tour/${tourId}`)
    }

    if(sites.status === Status.Ready && !this.state.bag.values.siteRef && sites.data.sites) {
      const defaultSite = sites.data.sites.filter(site => Site.mustRef(site.ref).id === DEFAULT_SITE_REF_ID)[0]
      this.setState({
        bag: {
          ...this.state.bag,
          values: {
            ...this.state.bag.values,
            siteRef: defaultSite ? Site.mustRef(defaultSite.ref) : undefined
          },
        }
      })
    }
  }

  private onClose = () => {
    this.props.onClose()
  }

  private onFormUpdate = (event: FormUpdateEvent<FormValues>) => {
    this.setState({
      bag: event.bag,
    })
  }

  private onFormValidate = (values: FormValues) => {
    const errors: FormErrors<FormValues> = {}

    if (!values.tourName || !values.tourName.trim().length) {
      errors.tourName = 'Tour name required';
    }
    return errors
  }

  private save = () => {
    const bag = validateFormBag(this.state.bag, this.onFormValidate)
    this.setState({ bag })

    if (!bag.valid) {
      return
    }

    const { tourName: name, description, siteRef } = bag.values

    const payload: CreateTourRequest.Entity = {
      ...CreateTourRequest.defaults,
      name: tourSchema.name ? tourSchema.name.write(name) : name,
      description: tourSchema.description ? tourSchema.description.write(description) : description,
      siteRef,
    }
    this.props.gettyCreateTourRequest(payload)
  }

  public render() {
    const { tourCreate, sites } = this.props
    const { bag: { errors, touched, valid } } = this.state
    const loading = tourCreate.status === Status.Loading

    const siteOpts = sites.status === Status.Ready && sites.data.sites
      ? sites.data.sites.map((site: Site.Entity) => ({
        label: site.name,
        value: Site.mustRef(site.ref),
      }))
      : []

    const footer = (
      <ButtonGroup>
        <Button
          type="button"
          key="cancel"
          appearance="secondary"
          onClick={(event) => { event.preventDefault(); this.onClose(); }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          isDisabled={loading || !valid}
          onClick={() => this.save()}
        >
          {loading ? 'Creating...' : 'Create'}
        </Button>
      </ButtonGroup>
    )

    return (
      <Modal onClose={this.onClose} header={<h3>Create tour</h3>} footer={footer}>
        <form noValidate onSubmit={(event) => { event.preventDefault(); this.save(); }}>
          <StatusGuard status={{ status: Status.Idle }} mode="form">
            <FormData
              bag={this.state.bag}
              onUpdate={this.onFormUpdate}
              validate={this.onFormValidate}
            >
              <Input
                label="Tour name"
                field="tourName"
                error={touched.tourName ? errors.tourName : undefined}
              />
              <SelectRef<FormValues, 'siteRef'>
                label="Site"
                field="siteRef"
                options={siteOpts}
                error={touched.siteRef ? errors.siteRef : undefined}
              />
              <FieldSection />
              <Input
                label="Description"
                field="description"
                component="textarea"
              />
            </FormData>
          </StatusGuard>
        </form>
      </Modal>
    )
  }
}

export const TourCreate = connect<ConnectedProps, ActionProps, DirectProps, GettyStore>(
  ({ getty }) => pick(getty, 'tourCreate', 'tourUpdate', 'sites'),
  {
    ...pick(actionCreators, 'gettyCreateTourRequest', 'gettyListSitesRequest'),
    ...pick(sharedActionCreators, 'toastNotification'),
  }
)(TourCreateView)
